<template>
  <a-card>
    <div class="cardBody">
      <a-form>
       <div v-if="fieldSettings.last_name.is_visible">
          <base-input :label="`Last Name`" :required="fieldSettings.last_name.is_required">
            <a-input placeholder="Last Name" v-model="formData.lastName"/>
            <div v-if="$v.formData.lastName.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.lastName.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.lastName.alphaNumSpecial">
                Special characters are not allowed
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.first_name.is_visible">
          <base-input :label="`First Name`" :required="fieldSettings.first_name.is_required">
            <a-input placeholder="First Name" v-model="formData.firstName"/>
            <div v-if="$v.formData.firstName.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.firstName.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.firstName.alphaNumSpecial">
                Special characters are not allowed
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.middle_name.is_visible">
          <base-input :label="`Middle Name`" :required="fieldSettings.middle_name.is_required">
            <a-input placeholder="Middle Name" v-model="formData.middleName"/>
            <div v-if="$v.formData.middleName.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.middleName.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.middleName.alphaNumSpecial">
                Special characters are not allowed
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.last_name_suffix.is_visible">
          <base-input :label="`Suffix`" :required="fieldSettings.last_name_suffix.is_required">
            <a-input
              placeholder="Suffix"
              v-model="formData.lastNameSuffix"
            />
            <div v-if="$v.formData.lastNameSuffix.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.lastNameSuffix.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.lastNameSuffix.alphaNumSpecial">
                Special characters are not allowed
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.nick_name.is_visible">
          <base-input :label="`Nick Name`" :required="fieldSettings.nick_name.is_required">
            <a-input placeholder="Nick Name" v-model="formData.nickName"/>
            <div v-if="$v.formData.nickName.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.nickName.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.nickName.alphaNumSpecial">
                Special characters are not allowed
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.gender.is_visible">
          <base-input :label="`Sex`" :required="fieldSettings.gender.is_required">
            <a-select placeholder="Sex" v-model="formData.gender">
              <a-select-option value="M">Male</a-select-option>
              <a-select-option value="F">Female</a-select-option>
            </a-select>
            <div v-if="$v.formData.gender.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.gender.required">
                Field is required
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.birth_month.is_visible && fieldSettings.birth_day.is_visible 
          && fieldSettings.birth_year.is_visible">
          <base-input :label="`Date of Birth`" 
          :required="fieldSettings.birth_month.is_required 
            && fieldSettings.birth_day.is_required 
            && fieldSettings.birth_year.is_required">
            <br />
            <a-date-picker
              placeholder="Birthday"
              :format="dateFormat"
              v-model="formData.birthDay"
            ></a-date-picker>
            <div v-if="$v.formData.birthDay.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.birthDay.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.birthDay.isLegalAge">
                Must be 18 years old and up
              </div>
            </div>
          </base-input>
        </div>
        <div v-if="fieldSettings.email.is_visible">
          <base-input :label="`Email Address`" :required="fieldSettings.email.is_required">
            <a-input placeholder="Email Address" v-model="formData.email" />
            <div v-if="$v.formData.email.$dirty" class="text-danger">
              <div class="error" v-if="!$v.formData.email.required">
                Field is required
              </div>
              <div class="error" v-if="!$v.formData.email.email">
                Invalid email format
              </div>
            </div>
          </base-input>
        </div>
      </a-form>
    </div>
  </a-card>
</template>

<script>
import { requiredIf, email } from 'vuelidate/lib/validators';
import moment from 'moment-timezone';
const TIMEZONE = process.env.VUE_APP_TIMEZONE || 'Asia/Singapore';
moment.tz.setDefault(TIMEZONE);
import { getFieldSettings } from '@/utils/clpSiteSettings';

const isLegalAge = (value) => {
  if (!value) return true;
  return moment().diff(moment(value), 'years') >= 18;
};

function alphaNumSpecial(value) {
  if (!value || typeof value !== 'string') return true;
  return /^[a-zA-Z0-9\s]+$/.test(value);
}

export default {
  props: {
    data: {
      type: Object,
    }
  },
  name: 'PersonalInformation',
  data() {
    return {
      dateFormat: 'YYYY-MM-DD',
      fieldSettings: { 
        last_name: { is_visible: 0, is_required: 0, is_editable: 0 },
        first_name: { is_visible: 0, is_required: 0, is_editable: 0 },
        middle_name: { is_visible: 0, is_required: 0, is_editable: 0 },
        last_name_suffix: { is_visible: 0, is_required: 0, is_editable: 0 },
        nick_name: { is_visible: 0, is_required: 0, is_editable: 0 },
        gender: { is_visible: 0, is_required: 0, is_editable: 0 },
        birth_month: { is_visible: 0, is_required: 0, is_editable: 0 },
        birth_day: { is_visible: 0, is_required: 0, is_editable: 0 },
        birth_year: { is_visible: 0, is_required: 0, is_editable: 0 },
        email: { is_visible: 0, is_required: 0, is_editable: 0 }
      },
      formData: {
        firstName: '',
        middleName: '',
        lastName: '',
        lastNameSuffix: '',
        nickName: '',
        gender: null,
        birthDay: null,
        email: null,
      },
    };
  },
  validations() {
    const validation = {
      formData: {
        firstName: {
          required: requiredIf(function () {
            return this.fieldSettings.first_name.is_required === true;
          }),
          alphaNumSpecial
        },
        middleName: {
          required: requiredIf(function () {
            return this.fieldSettings.middle_name.is_required === true;
          }),
          alphaNumSpecial
        },
        lastName: {
          required: requiredIf(function () {
            return this.fieldSettings.last_name.is_required === true;
          }),
          alphaNumSpecial
        },
        lastNameSuffix: {
          required: requiredIf(function () {
            return this.fieldSettings.last_name_suffix.is_required === true;
          }),
          alphaNumSpecial
        },
        nickName: {
          required: requiredIf(function () {
            return this.fieldSettings.nick_name.is_required === true;
          }),
          alphaNumSpecial
        },
        gender: {
          required: requiredIf(function () {
            return this.fieldSettings.gender.is_required === true;
          })
        },
        birthDay: {  
          required: requiredIf(function () {
            return this.fieldSettings.birth_day.is_required === true;
          }), isLegalAge 
        },
        email: { 
          required: requiredIf(function () {
            return this.fieldSettings.email.is_required === true;
          }), email 
        },
      }
    };
    return validation;
  },
  async created() {
    this.fieldSettings = await getFieldSettings();
    console.log('Field Settings:', this.fieldSettings);
    await this.populateData();
  },
  methods: {
    populateData() {
      const data = this.data;
      this.formData.firstName = data.firstName;
      this.formData.middleName = data.middleName;
      this.formData.lastName = data.lastName;
      this.formData.lastNameSuffix = data.lastNameSuffix;
      this.formData.nickName = data.nickName;
      this.formData.gender = data.gender;
      this.formData.birthDay = data.birthDay;
      this.formData.email = data.email;
    },
  },
  watch: {
    'this.fieldSettings': {
      immediate: true,
      handler(newVal) {
        this.fieldSettings = newVal;
      },
    },
  },
};
</script>

<style></style>
